/**
 * Duplicate Draft Modal – duplicate selected drafts with copies count and target date.
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC, useMemo, useState } from 'react';
import { Calendar, Copy, Hash, X } from 'lucide-react';

import { Button } from '@/components/ui';
import { Input } from '@/components/ui';
import { formatDateTimeDMYHM } from '@/features/smswiser-campaigns/utils/dateFormat';
import { shiftedExecuteAfterForCopiedDrop } from '@/features/smswiser-campaigns/utils/shiftedExecuteAfterForCopiedDrop';
import { backdropClick } from '@/components/ui';

/** Just enough of a draft to work out where its copy would be sent. */
export interface DuplicateSourceSchedule {
  workDate: string;
  executeAfter?: string | null;
}

export interface DuplicateDraftModalProps {
  count: number;
  defaultDate?: string;
  /** The selected drafts, so the modal can show where their copies will land. */
  sources?: DuplicateSourceSchedule[];
  isBusy: boolean;
  onClose: () => void;
  onConfirm: (copiesPerDraft: number, targetWorkDate: string) => void | Promise<void>;
}

export const DuplicateDraftModal: FC<DuplicateDraftModalProps> = ({
  count,
  defaultDate,
  sources,
  isBusy,
  onClose,
  onConfirm,
}) => {
  const [copiesInput, setCopiesInput] = useState('1');
  const [targetDate, setTargetDate] = useState(defaultDate ?? '');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const copiesNum = (() => {
    const n = parseInt(copiesInput.trim(), 10);
    if (!Number.isFinite(n) || n < 1) return 1;
    return Math.min(100, n);
  })();
  const totalNew = count * copiesNum;

  /**
   * Where the copies actually land.
   *
   * A draft that belongs to one day but is sent early the next keeps that gap,
   * so the target date is not always the send date. That is the surprising part
   * of duplicating, and it belongs on screen before the click rather than in the
   * list afterwards.
   */
  const preview = useMemo(() => {
    const target = targetDate.trim();
    const scheduled = (sources ?? []).filter((s) => s.executeAfter?.trim());
    if (!target || scheduled.length === 0) return null;

    const sends = scheduled.map((s) =>
      shiftedExecuteAfterForCopiedDrop(s.executeAfter, target, s.workDate),
    );
    const distinct = [...new Set(sends.filter(Boolean) as string[])];
    if (distinct.length === 0) return null;

    const nextDayCount = sends.filter(
      (iso) => iso && !formatDateTimeDMYHM(iso).startsWith(ymdToDmy(target)),
    ).length;

    return {
      single: distinct.length === 1 ? formatDateTimeDMYHM(distinct[0]) : null,
      nextDayCount,
      total: scheduled.length,
    };
  }, [sources, targetDate]);

  const handleConfirm = async () => {
    if (!targetDate.trim()) return;
    const n = copiesNum;
    setIsSubmitting(true);
    try {
      const result = onConfirm(n, targetDate.trim());
      if (result && typeof (result as Promise<unknown>).then === 'function') {
        await (result as Promise<void>);
      }
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-background/90 p-4 backdrop-blur-sm"
      onClick={backdropClick(onClose)}
    >
      <div
        className="flex w-full max-w-md flex-col overflow-hidden rounded-xl border border-border bg-card shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-center justify-between border-b border-border bg-muted/20 px-4 py-3">
          <h3 className="flex items-center gap-2 text-base font-semibold">
            <Copy className="h-4 w-4" />
            Duplicate drafts
          </h3>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onClose} aria-label="Close">
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="flex flex-col gap-4 p-4">
          <p className="text-sm text-muted-foreground">
            Create <strong>{copiesNum}</strong> copies of each of the <strong>{count}</strong> selected
            draft{count !== 1 ? 's' : ''} ({totalNew} new draft{totalNew !== 1 ? 's' : ''} total).
          </p>

          <div className="flex flex-col gap-2">
            <label className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
              <Calendar className="h-3.5 w-3.5" />
              Target date
            </label>
            <Input
              type="date"
              className="h-10 w-full"
              value={targetDate}
              onChange={(e) => setTargetDate(e.target.value)}
              aria-label="Target date for duplicated drafts"
            />
          </div>

          {preview && (
            <div className="rounded-lg border border-primary/20 bg-primary/5 px-3 py-2 text-xs leading-relaxed">
              {preview.single ? (
                <>
                  Planned send:{' '}
                  <span className="font-medium">{preview.single}</span>
                  {preview.nextDayCount > 0 && (
                    <span className="text-muted-foreground"> · the day after</span>
                  )}
                </>
              ) : (
                <>
                  Each copy keeps its own send time
                  {preview.nextDayCount > 0 && (
                    <>
                      , and{' '}
                      <span className="font-medium">
                        {preview.nextDayCount} of {preview.total}
                      </span>{' '}
                      land the day after the target date
                    </>
                  )}
                  .
                </>
              )}
            </div>
          )}

          <div className="flex flex-col gap-2">
            <label className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
              <Hash className="h-3.5 w-3.5" />
              Copies per draft (1–100)
            </label>
            <Input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              className="h-10"
              placeholder="e.g. 1"
              value={copiesInput}
              onChange={(e) => {
                const v = e.target.value.replace(/\D/g, '');
                setCopiesInput(v || '');
              }}
              aria-label="Number of copies per draft"
            />
          </div>

          <div className="flex justify-end gap-2 border-t border-border pt-4">
            <Button variant="outline" onClick={onClose} disabled={isBusy || isSubmitting}>
              Cancel
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={isBusy || isSubmitting || !targetDate.trim()}
            >
              {isSubmitting ? 'Duplicating…' : 'Duplicate'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

/** "2026-08-14" → "14/08/2026", to compare against a formatted send. */
function ymdToDmy(ymd: string): string {
  const m = ymd.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : ymd;
}

export default DuplicateDraftModal;
